import { FC, ReactNode, useState, useEffect } from 'react';
import styles from './AppLayout.module.scss';
import { Sidebar } from '../Sidebar';
import { TopHeader, BreadcrumbItem } from '../TopHeader';

export interface AppLayoutProps {
  children: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  searchPlaceholder?: string;
}

export const AppLayout: FC<AppLayoutProps> = ({
  children,
  breadcrumbs,
  searchPlaceholder,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
  );

  // Handle window resize between mobile and desktop breakpoints
  useEffect(() => {
    let lastWidth = window.innerWidth;
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      if (lastWidth < 1024 && currentWidth >= 1024) {
        setSidebarOpen(true);
      } else if (lastWidth >= 1024 && currentWidth < 1024) {
        setSidebarOpen(false);
      }
      lastWidth = currentWidth;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent background scroll when mobile sidebar is open
  useEffect(() => {
    if (window.innerWidth < 1024 && sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  return (
    <div className={styles.layoutRoot}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={`${styles.mainWrapper} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
        <TopHeader
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
          breadcrumbs={breadcrumbs}
          searchPlaceholder={searchPlaceholder}
        />
        <div className={styles.contentArea}>{children}</div>
      </div>
    </div>
  );
};
